import { fmtMoney } from '../lib/format';
import { Button, Field, Input, Select } from './ui';
import { IconPlus, IconTrash } from './icons';

/** Morning payment types (developers.morning.co — PaymentGroup). */
export const PAYMENT_TYPES = { 1: 'מזומן', 2: 'צ׳ק', 3: 'כרטיס אשראי', 4: 'העברה בנקאית', 10: 'אפליקציית תשלום (ביט / פייבוקס)', 0: 'ניכוי במקור', 11: 'אחר' } as const;
export type PaymentType = 0 | 1 | 2 | 3 | 4 | 10 | 11;

export type Payment = {
  type: PaymentType;
  date: string;
  amount: number;
  chequeNum?: string | null;
  bankName?: string | null;
  bankBranch?: string | null;
  bankAccount?: string | null;
  cardNum?: string | null;
  numPayments?: number | null;
};

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jerusalem' });
export const newPayment = (amount: number, type: PaymentType = 4): Payment => ({ type, date: today(), amount: Math.round(amount * 100) / 100 });

/**
 * How a חשבונית מס/קבלה was paid — one row per payment (several cheques = several rows).
 * The rows must add up to the document total including VAT.
 */
export function PaymentRows({ payments, total, onChange, disabled }: { payments: Payment[]; total: number; onChange: (p: Payment[]) => void; disabled?: boolean }) {
  const sum = Math.round(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0) * 100) / 100;
  const diff = Math.round((total - sum) * 100) / 100;
  const set = (i: number, patch: Partial<Payment>) => onChange(payments.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="stack" style={{ gap: 10 }}>
      {payments.map((p, i) => (
        <div key={i} className="stack" style={{ gap: 6, borderBottom: '1px solid var(--line)', paddingBottom: 8 }}>
          <div className="row" style={{ gap: 6 }}>
            <Select value={p.type} disabled={disabled} onChange={(e) => set(i, { type: Number(e.target.value) as PaymentType })} aria-label="אמצעי תשלום" style={{ flex: 1 }}>
              {Object.entries(PAYMENT_TYPES).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </Select>
            {payments.length > 1 && !disabled ? (
              <Button size="sm" variant="ghost" icon onClick={() => onChange(payments.filter((_, j) => j !== i))} aria-label="הסרת תשלום"><IconTrash size={14} /></Button>
            ) : null}
          </div>
          <div className="form-grid">
            <Field label="סכום (כולל מע״מ)"><Input inputMode="decimal" value={String(p.amount)} disabled={disabled} onChange={(e) => set(i, { amount: Number(e.target.value.replace(/[₪,\s]/g, '')) || 0 })} ltr /></Field>
            <Field label="תאריך"><Input type="date" value={p.date} disabled={disabled} onChange={(e) => set(i, { date: e.target.value })} ltr /></Field>
          </div>
          {p.type === 2 ? (
            <div className="form-grid">
              <Field label="מספר צ׳ק" required><Input value={p.chequeNum ?? ''} disabled={disabled} onChange={(e) => set(i, { chequeNum: e.target.value })} ltr /></Field>
              <Field label="בנק"><Input value={p.bankName ?? ''} disabled={disabled} onChange={(e) => set(i, { bankName: e.target.value })} /></Field>
              <Field label="סניף"><Input value={p.bankBranch ?? ''} disabled={disabled} onChange={(e) => set(i, { bankBranch: e.target.value })} ltr /></Field>
              <Field label="חשבון"><Input value={p.bankAccount ?? ''} disabled={disabled} onChange={(e) => set(i, { bankAccount: e.target.value })} ltr /></Field>
            </div>
          ) : null}
          {p.type === 3 ? (
            <div className="form-grid">
              <Field label="4 ספרות אחרונות" hint="לעולם לא את מספר הכרטיס המלא"><Input value={p.cardNum ?? ''} maxLength={4} disabled={disabled} onChange={(e) => set(i, { cardNum: e.target.value.replace(/\D/g, '').slice(0, 4) })} ltr /></Field>
              <Field label="מספר תשלומים"><Input inputMode="numeric" value={p.numPayments ?? ''} disabled={disabled} onChange={(e) => set(i, { numPayments: Number(e.target.value) || null })} ltr /></Field>
            </div>
          ) : null}
        </div>
      ))}
      <div className="row wrap" style={{ gap: 8 }}>
        {!disabled ? (
          <Button size="sm" variant="ghost" onClick={() => onChange([...payments, newPayment(Math.max(0, diff), payments[payments.length - 1]?.type ?? 2)])}>
            <IconPlus /> תשלום נוסף
          </Button>
        ) : null}
        <span className="spacer" />
        {Math.abs(diff) > 0.009 ? (
          <span className="small" style={{ color: 'var(--warn)' }}>
            {diff > 0 ? `חסר ${fmtMoney(diff)}` : `עודף ${fmtMoney(-diff)}`} עד סכום המסמך ({fmtMoney(total)})
            {!disabled && payments.length === 1 ? <> · <button type="button" className="link-button" onClick={() => set(0, { amount: total })}>להשוות</button></> : null}
          </span>
        ) : (
          <span className="small" style={{ color: 'var(--ok)' }}>התשלומים מכסים את המסמך</span>
        )}
      </div>
    </div>
  );
}
